import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { 
  UrgentConsulting, 
  CreateUrgentConsultingRequest, 
  UpdateUrgentConsultingRequest 
} from '../types/api';

export const QUERY_KEYS = {
  URGENT_CONSULTATIONS: 'urgent_consultations',
  URGENT_CONSULTATION: 'urgent_consultation',
} as const;

// Fetch all urgent consultations
export const useUrgentConsultations = (limit?: number, offset?: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS, { limit, offset }],
    queryFn: () => apiService.getAllUrgentConsultations(limit, offset),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
    refetchOnWindowFocus: true,
  });
};

// Fetch open urgent consultations without limit/offset
export const useOpenUrgentConsultations = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS, 'open'],
    queryFn: () => apiService.getOpenUrgentConsultations(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
    refetchOnWindowFocus: true,
  });
};

// Fetch done urgent consultations
export const useDoneUrgentConsultations = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS, 'done'],
    queryFn: () => apiService.getDoneUrgentConsultations(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
    refetchOnWindowFocus: true,
  });
};

// Fetch need patient meeting urgent consultations
export const useNeedPatientMeetingUrgentConsultations = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS, 'needPatientMeeting'],
    queryFn: () => apiService.getNeedPatientMeetingUrgentConsultations(),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute for real-time updates
    refetchOnWindowFocus: true,
  });
};

// Fetch single urgent consultation
export const useUrgentConsultation = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.URGENT_CONSULTATION, id],
    queryFn: () => apiService.getUrgentConsultingById(id),
    enabled: !!id,
    staleTime: 30000,
  });
};

// Create urgent consultation
export const useCreateUrgentConsulting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUrgentConsultingRequest) => apiService.createUrgentConsulting(data),
    onSuccess: () => {
      // Invalidate and refetch consultations list
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS] });
    },
    onError: (error) => {
      console.error('Failed to create urgent consulting:', error);
    },
  });
};

// Update urgent consultation
export const useUpdateUrgentConsulting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUrgentConsultingRequest }) =>
      apiService.updateUrgentConsulting(id, data),
    onSuccess: (updatedConsultation) => {
      // Update the consultations list cache
      queryClient.setQueryData<UrgentConsulting[]>(
        [QUERY_KEYS.URGENT_CONSULTATIONS],
        (old) =>
          old?.map((consultation) =>
            consultation.id === updatedConsultation.id ? updatedConsultation : consultation
          ) || []
      );

      // Update individual consultation cache
      queryClient.setQueryData(
        [QUERY_KEYS.URGENT_CONSULTATION, updatedConsultation.id],
        updatedConsultation
      );
    },
    onError: (error) => {
      console.error('Failed to update urgent consulting:', error);
    },
  });
};

// Need patient meeting action
export const useNeedPatientMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (callId: number) => apiService.needPatientMeeting(callId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS] });
    },
    onError: (error) => {
      console.error('Failed to request patient meeting:', error);
    },
  });
};

// Make conference call action
export const useMakeConferenceCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (callId: number) => apiService.makeConferenceCall(callId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS] });
    },
    onError: (error) => {
      console.error('Failed to make conference call:', error);
    },
  });
};

// Conference call done action
export const useConferenceCallDone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (callId: number) => apiService.conferenceCallDone(callId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.URGENT_CONSULTATIONS] });
    },
    onError: (error) => {
      console.error('Failed to complete conference call:', error);
    },
  });
};